import { Fragment, useMemo } from 'react';
import { uid } from 'react-uid';
import { TypographyOwnProps } from '@mui/material';

import { useDataTableStyles } from './useDataTableStyles';
import { BaseTableData } from '../../../types';
import { DataTableItem } from './DataTableItem';

interface DataTableProps {
  data: BaseTableData[];
  variant?: TypographyOwnProps['variant'];
  headingTitles: string[] | JSX.Element[];
}

/**
 * supports 2 and 3 columns
 * */

export const DataTable = ({
  data,
  variant,
  headingTitles,
}: DataTableProps): JSX.Element => {
  const { classes, cx } = useDataTableStyles();

  const isThreeColumnsTable = headingTitles.length === 3;

  const renderHeadingTitles = useMemo(
    () =>
      headingTitles.map((headingTitle, idx) => {
        const isSecondColumn = idx === 1;
        const hasPaddingRight = isSecondColumn && isThreeColumnsTable;

        return (
          <DataTableItem
            key={uid(headingTitle)}
            className={cx(classes.column, classes.columnHeader, {
              [classes.columnTwo]: isSecondColumn,
              [classes.withPaddingRight]: hasPaddingRight,
            })}
            variant={variant}
            value={headingTitle}
          />
        );
      }),
    [classes, cx, headingTitles, isThreeColumnsTable, variant],
  );

  if (isThreeColumnsTable) {
    return (
      <div className={classes.tableContainerWithThreeColumns}>
        {renderHeadingTitles}
        {data.map(({ label, value, extra = '' }, idx) => {
          return (
            <Fragment key={idx}>
              <DataTableItem
                longText
                className={classes.column}
                variant={variant}
                value={label}
              />
              <DataTableItem
                className={cx(
                  classes.column,
                  classes.columnTwo,
                  classes.withPaddingRight,
                )}
                variant={variant}
                value={value}
              />
              <DataTableItem
                className={classes.column}
                variant={variant}
                value={extra}
              />
            </Fragment>
          );
        })}
      </div>
    );
  }

  return (
    <div className={classes.tableContainerWithTwoColumns}>
      {renderHeadingTitles}
      {data.map(({ label, value }, idx) => {
        return (
          <Fragment key={idx}>
            <DataTableItem
              longText
              className={classes.column}
              variant={variant}
              value={label}
            />
            <DataTableItem
              className={cx(classes.column, classes.columnTwo)}
              variant={variant}
              value={value}
            />
          </Fragment>
        );
      })}
    </div>
  );
};
